import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as marketingService from '../../services/marketingService';
import * as eventsService from '../../services/eventsService';

const {
  FiSend,
  FiUsers,
  FiPlus,
  FiTrash2,
  FiEye,
  FiX,
  FiSave,
  FiClock,
  FiFileText,
} = FiIcons;

const AUDIENCE_TYPES = [
  { value: 'registered', label: 'Registered attendees' },
  { value: 'leads', label: 'Leads' },
  { value: 'unpaid_leads', label: 'Unpaid leads only' },
  { value: 'both', label: 'Both (registered + leads)' },
  { value: 'custom', label: 'Custom — enter emails manually' },
];

const CONTENT_MODES = [
  { value: 'template', label: 'Saved template' },
  { value: 'raw', label: 'Raw HTML with merge tags' },
];

const SCHEDULE_MODES = [
  { value: 'immediate', label: 'Send immediately' },
  { value: 'scheduled', label: 'Schedule for a specific date/time' },
  { value: 'multiple', label: 'Multiple send times' },
];

const audienceLabel = (audience) => {
  if (audience === 'registered') return 'Registered';
  if (audience === 'lead') return 'Lead';
  if (audience === 'custom') return 'Custom';
  return audience || '—';
};

/** Split pasted comma/semicolon/newline-separated emails into rows */
const parsePastedEmails = (text) => {
  const parts = text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s && s.includes('@'));
  return parts;
};

/** Convert datetime-local value to ISO string for email_queue.send_at */
const localInputToIso = (localValue) => {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const defaultLocalDateTime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const MarketingQuickSend = () => {
  const { profile } = useAuth();
  const authOpts = useMemo(
    () => ({ role: 'admin', userId: profile?.id }),
    [profile?.id],
  );

  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [audienceType, setAudienceType] = useState('registered');
  const [contentMode, setContentMode] = useState('raw');
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [scheduleMode, setScheduleMode] = useState('immediate');
  const [scheduledAt, setScheduledAt] = useState(defaultLocalDateTime);
  const [sendTimes, setSendTimes] = useState([defaultLocalDateTime()]);

  const [recipientList, setRecipientList] = useState([{ email: '', name: '' }]);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showAudiencePreview, setShowAudiencePreview] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);

  const isCustomAudience = audienceType === 'custom';

  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const loadTemplates = useCallback(async () => {
    const { data, error } = await marketingService.listTemplates(authOpts);
    if (!error && data) setTemplates(Array.isArray(data) ? data : []);
  }, [authOpts]);

  const loadEvents = useCallback(async () => {
    const { data, error } = await eventsService.listEvents(authOpts);
    if (!error && data) setEvents(Array.isArray(data) ? data : []);
  }, [authOpts]);

  useEffect(() => {
    loadTemplates();
    loadEvents();
  }, [loadTemplates, loadEvents]);

  useEffect(() => {
    if (contentMode === 'template' && templateId && templates.length) {
      const t = templates.find((x) => x.id === templateId);
      if (t) {
        setSubject(t.subject ?? '');
        setBody(t.html_body ?? '');
      }
    }
  }, [templateId, templates, contentMode]);

  const loadRecipients = useCallback(async () => {
    if (isCustomAudience) {
      const hasEmails = recipientList.some((r) => (r.email || '').trim());
      if (!eventId || !hasEmails) {
        setRecipients(
          hasEmails
            ? recipientList
                .filter((r) => (r.email || '').trim())
                .map((r) => ({
                  email: r.email.trim(),
                  name: r.name?.trim() || undefined,
                  lead_id: null,
                  audience: 'custom',
                }))
            : [],
        );
        return;
      }
      setLoadingRecipients(true);
      const { data, error } = await marketingService.getRecipientsForSegment(
        { segment_type: 'custom', event_id: eventId, recipient_list: recipientList },
        authOpts,
      );
      setLoadingRecipients(false);
      if (error) {
        toast.error(error.message ?? 'Failed to load audience');
        setRecipients([]);
        return;
      }
      setRecipients(Array.isArray(data) ? data : []);
      return;
    }

    if (!eventId) {
      setRecipients([]);
      return;
    }
    setLoadingRecipients(true);
    const { data, error } = await marketingService.getRecipientsForSegment(
      { segment_type: audienceType, event_id: eventId },
      authOpts,
    );
    setLoadingRecipients(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load audience');
      setRecipients([]);
      return;
    }
    setRecipients(Array.isArray(data) ? data : []);
  }, [audienceType, eventId, authOpts, isCustomAudience, recipientList]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const recipientsWithLead = recipients.filter((r) => r.lead_id);
  const recipientsMissingLead = recipients.length - recipientsWithLead.length;

  const addRecipientRow = () => setRecipientList((r) => [...r, { email: '', name: '' }]);
  const removeRecipientRow = (i) =>
    setRecipientList((r) => (r.length <= 1 ? [{ email: '', name: '' }] : r.filter((_, idx) => idx !== i)));
  const updateRecipient = (i, field, value) => {
    setRecipientList((r) => {
      const next = [...r];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };
  const handleRecipientEmailPaste = (i, pasted) => {
    const emails = parsePastedEmails(pasted);
    if (emails.length <= 1) return;
    setRecipientList((r) => {
      const next = [...r];
      next[i] = { ...next[i], email: emails[0] };
      for (let j = 1; j < emails.length; j += 1) {
        next.push({ email: emails[j], name: '' });
      }
      return next;
    });
  };

  const buildSendAtTimes = () => {
    if (scheduleMode === 'immediate') return [null];
    if (scheduleMode === 'scheduled') {
      const iso = localInputToIso(scheduledAt);
      return iso ? [iso] : [];
    }
    return sendTimes.map((t) => localInputToIso(t)).filter(Boolean);
  };

  const emailableCount = recipients.filter((r) => (r.email || '').trim()).length;
  const totalQueueRows = emailableCount * buildSendAtTimes().length;

  const addSendTime = () => setSendTimes((t) => [...t, defaultLocalDateTime()]);
  const removeSendTime = (i) => setSendTimes((t) => (t.length <= 1 ? t : t.filter((_, idx) => idx !== i)));
  const updateSendTime = (i, value) => {
    setSendTimes((t) => {
      const next = [...t];
      next[i] = value;
      return next;
    });
  };

  const addNineHours = () => {
    const d = new Date();
    d.setHours(d.getHours() + 9);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    setSendTimes((t) => [...t, d.toISOString().slice(0, 16)]);
  };

  const setNowAndNineHours = () => {
    const now = new Date();
    const later = new Date();
    later.setHours(later.getHours() + 9);
    const fmt = (d) => {
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      return d.toISOString().slice(0, 16);
    };
    setSendTimes([fmt(now), fmt(later)]);
  };

  const openEmailPreview = async () => {
    if (!subject.trim() && !body.trim()) {
      toast.error('Enter a subject or body to preview.');
      return;
    }
    setShowEmailPreview(true);
    setEmailPreviewLoading(true);
    setEmailPreview(null);
    const firstRecipient = recipients.find((r) => (r.email || '').trim()) ?? null;
    const { data, error } = await marketingService.previewQuickSendEmail(
      {
        subject,
        body,
        event_id: eventId || null,
        recipient: firstRecipient,
      },
      authOpts,
    );
    setEmailPreviewLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load preview');
      return;
    }
    setEmailPreview(data);
  };

  const handleSaveAsTemplate = async () => {
    const name = saveTemplateName.trim();
    if (!name) {
      toast.error('Template name is required.');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required to save a template.');
      return;
    }
    setSavingTemplate(true);
    const { data, error } = await marketingService.createTemplate(
      {
        name,
        subject: subject.trim(),
        html_body: body.trim(),
        category: 'marketing',
        trigger_event: 'custom',
      },
      authOpts,
    );
    setSavingTemplate(false);
    if (error) {
      toast.error(error.message ?? 'Failed to save template');
      return;
    }
    toast.success('Template saved.');
    setShowSaveTemplate(false);
    setSaveTemplateName('');
    await loadTemplates();
    if (data?.id) {
      setContentMode('template');
      setTemplateId(data.id);
    }
  };

  const handleSend = async () => {
    const sendAtTimes = buildSendAtTimes();
    if (scheduleMode !== 'immediate' && sendAtTimes.length === 0) {
      toast.error('Add at least one valid send date/time.');
      return;
    }
    if (emailableCount === 0) {
      toast.error('No recipients in the audience.');
      return;
    }
    if (!eventId) {
      toast.error('Select an event.');
      return;
    }
    if (!subject.trim()) {
      toast.error('Subject is required.');
      return;
    }
    if (!body.trim()) {
      toast.error('Body is required.');
      return;
    }

    setSending(true);
    const emailSendPayload = {
      subject: subject.trim(),
      body: body.trim(),
      template_id: templateId || null,
      segment_type: audienceType,
      segment_filters: { event_id: eventId },
      recipient_list: isCustomAudience
        ? recipients.map((r) => ({ email: r.email, name: r.name }))
        : null,
      total_recipients: emailableCount,
    };
    const { data: sendRecord, error: sendError } = await marketingService.createEmailSend(
      emailSendPayload,
      authOpts,
    );
    if (sendError) {
      setSending(false);
      toast.error(sendError.message ?? 'Failed to create send record');
      return;
    }

    const { data: queueResult, error: queueError } = await marketingService.queueQuickSendBlast(
      {
        event_id: eventId,
        recipients,
        subject: subject.trim(),
        body: body.trim(),
        template_id: templateId || null,
        send_at_times: sendAtTimes,
      },
      { ...authOpts, email_send_id: sendRecord?.id },
    );

    if (queueError) {
      toast.error(queueError.message ?? 'Failed to queue emails.');
    } else {
      const queued = queueResult?.queued ?? totalQueueRows;
      const skipped = queueResult?.skipped ?? 0;
      let msg = `Queued ${queued} email(s) for ${queueResult?.recipients ?? recipientsWithLead.length} recipient(s).`;
      if (skipped > 0) msg += ` ${skipped} skipped (no context).`;
      if (scheduleMode !== 'immediate') msg += ' They will send at the scheduled time(s).';
      toast.success(msg);
    }

    if (sendRecord?.id) {
      await marketingService.updateEmailSend(
        sendRecord.id,
        { status: 'sending', sent_count: 0 },
        authOpts,
      );
    }

    setSending(false);
    setShowConfirm(false);
    if (!queueError) {
      setSubject('');
      setBody('');
      setTemplateId('');
      setContentMode('raw');
      setRecipientList([{ email: '', name: '' }]);
    }
  };

  const canSend =
    eventId &&
    subject.trim() &&
    body.trim() &&
    emailableCount > 0 &&
    (scheduleMode === 'immediate' || buildSendAtTimes().length > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-1">Quick Send</h3>
        <p className="text-sm text-slate-500 mb-6">
          One-off email blast for an event. Merge tags resolve per recipient at send time via{' '}
          <code className="text-xs bg-slate-100 px-1 rounded">build_workflow_context</code>.
        </p>

        <div className="space-y-6">
          <section className="space-y-4 pb-6 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Audience</h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Event <span className="text-red-500">*</span>
              </label>
              <select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Select event</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Audience</label>
              <select
                value={audienceType}
                onChange={(e) => setAudienceType(e.target.value)}
                disabled={!eventId}
                className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                {AUDIENCE_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {loadingRecipients ? (
                <span className="text-slate-500 flex items-center gap-2 text-sm">
                  <span className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  Loading audience…
                </span>
              ) : (
                <span className="text-slate-600 flex items-center gap-2 text-sm">
                  <SafeIcon icon={FiUsers} className="w-4 h-4" />
                  <strong>{recipients.length}</strong> in audience
                  {recipientsMissingLead > 0 && (
                    <span className="text-amber-700">
                      ({recipientsWithLead.length} full merge context
                      {recipientsMissingLead > 0
                        ? `, ${recipientsMissingLead} event/basic tags only`
                        : ''}
                      )
                    </span>
                  )}
                </span>
              )}
              <button
                type="button"
                onClick={() => setShowAudiencePreview(true)}
                disabled={!eventId || recipients.length === 0}
                className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm font-medium disabled:opacity-50"
              >
                <SafeIcon icon={FiEye} className="w-4 h-4" />
                Preview audience
              </button>
            </div>
            {isCustomAudience && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email addresses
                </label>
                <p className="text-xs text-slate-500 mb-2">
                  Add one or more recipients. Paste multiple addresses separated by commas or
                  newlines into an email field.
                </p>
                <div className="space-y-2 max-w-2xl">
                  {recipientList.map((r, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        type="email"
                        value={r.email}
                        onChange={(e) => updateRecipient(i, 'email', e.target.value)}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData('text');
                          if (parsePastedEmails(pasted).length > 1) {
                            e.preventDefault();
                            handleRecipientEmailPaste(i, pasted);
                          }
                        }}
                        placeholder="email@example.com"
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                      />
                      <input
                        type="text"
                        value={r.name}
                        onChange={(e) => updateRecipient(i, 'name', e.target.value)}
                        placeholder="Name (optional)"
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeRecipientRow(i)}
                        className="p-2 text-slate-400 hover:text-red-600"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addRecipientRow}
                    className="flex items-center gap-2 text-sm text-emerald-600 hover:underline"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add recipient
                  </button>
                </div>
              </div>
            )}
          </section>

          <section className="space-y-4 pb-6 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Content</h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Content source</label>
              <select
                value={contentMode}
                onChange={(e) => {
                  setContentMode(e.target.value);
                  if (e.target.value === 'raw') setTemplateId('');
                }}
                className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
              >
                {CONTENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            {contentMode === 'template' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Template</label>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select template</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g. Reminder: {{event_title}} on {{event_date}}"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Body (HTML) <span className="text-red-500">*</span>
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
                rows={12}
                placeholder="<p>Hello {{student_name}}, ...</p>"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openEmailPreview}
                disabled={!subject.trim() && !body.trim()}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-50 font-medium"
              >
                <SafeIcon icon={FiEye} className="w-4 h-4" />
                Preview Email
              </button>
              {contentMode === 'raw' && (
                <button
                  type="button"
                  onClick={() => {
                    setSaveTemplateName('');
                    setShowSaveTemplate(true);
                  }}
                  disabled={!subject.trim() || !body.trim()}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-50"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  Save as template
                </button>
              )}
            </div>
          </section>

          <section className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Schedule</h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">When to send</label>
              <select
                value={scheduleMode}
                onChange={(e) => setScheduleMode(e.target.value)}
                className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
              >
                {SCHEDULE_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            {scheduleMode === 'scheduled' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                  <SafeIcon icon={FiClock} className="w-4 h-4" />
                  Send at
                </label>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            )}
            {scheduleMode === 'multiple' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Send times</label>
                {sendTimes.map((t, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      type="datetime-local"
                      value={t}
                      onChange={(e) => updateSendTime(i, e.target.value)}
                      className="flex-1 max-w-xs px-4 py-2 border border-gray-200 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeSendTime(i)}
                      disabled={sendTimes.length <= 1}
                      className="p-2 text-slate-400 hover:text-red-600 disabled:opacity-30"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={addSendTime}
                    className="flex items-center gap-2 text-sm text-emerald-600 hover:underline"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add send time
                  </button>
                  <button
                    type="button"
                    onClick={addNineHours}
                    className="text-sm text-slate-600 hover:underline"
                  >
                    + Add in 9 hours
                  </button>
                  <button
                    type="button"
                    onClick={setNowAndNineHours}
                    className="text-sm text-slate-600 hover:underline"
                  >
                    Now + 9 hours
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Each recipient gets one queue row per send time ({recipientsWithLead.length} ×{' '}
                  {sendTimes.length} = {recipientsWithLead.length * sendTimes.length} emails if all
                  times are valid).
                </p>
              </div>
            )}
          </section>

          <div className="pt-4">
            <button
              onClick={() => setShowConfirm(true)}
              disabled={!canSend || sending}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <SafeIcon icon={FiSend} className="w-4 h-4" />
              Queue email blast
            </button>
          </div>
        </div>
      </div>

      {showAudiencePreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowAudiencePreview(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-start gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Preview audience</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Read-only list of recipients for the selected event and audience. Rows with a
                  lead id get full merge tags; others use event-level tags when available.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAudiencePreview(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-3 border-b border-gray-100 bg-slate-50 text-sm font-medium text-slate-700">
              {recipients.length} recipient{recipients.length === 1 ? '' : 's'}
              <span className="ml-2 text-xs font-normal text-slate-500">
                ({recipientsWithLead.length} with full merge context)
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {recipients.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <SafeIcon icon={FiUsers} className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="font-medium">No recipients for this selection</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-white sticky top-0 border-b border-gray-100">
                    <tr className="text-left text-slate-600">
                      <th className="px-4 py-2.5 font-medium">Name</th>
                      <th className="px-4 py-2.5 font-medium">Email</th>
                      <th className="px-4 py-2.5 font-medium">Type</th>
                      <th className="px-4 py-2.5 font-medium">Lead id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r, idx) => (
                      <tr key={`${r.email}-${idx}`} className="border-b border-gray-50">
                        <td className="px-4 py-3 text-slate-800">{r.name || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{r.email}</td>
                        <td className="px-4 py-3 text-slate-600">{audienceLabel(r.audience)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {r.lead_id ? (
                            <span title={r.lead_id}>{r.lead_id.slice(0, 8)}…</span>
                          ) : (
                            <span className="text-amber-600">Basic only</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {showEmailPreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowEmailPreview(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-start gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Preview email</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Read-only preview. No emails are sent from this action.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowEmailPreview(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            {emailPreview?.preview_label && (
              <div className="px-6 py-2.5 border-b border-gray-100 bg-slate-50 text-xs text-slate-600">
                {emailPreview.preview_label}
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50">
              {emailPreviewLoading ? (
                <div className="flex justify-center py-16">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : emailPreview ? (
                <>
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Subject
                    </p>
                    <p className="text-slate-800 font-medium break-words">
                      {emailPreview.subject || '(empty)'}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide px-4 pt-4 pb-2">
                      Body
                    </p>
                    {emailPreview.body ? (
                      <div
                        className="px-4 pb-4 prose prose-sm max-w-none border-t border-gray-50 pt-4"
                        dangerouslySetInnerHTML={{ __html: emailPreview.body }}
                      />
                    ) : (
                      <p className="px-4 pb-4 text-slate-400 text-sm">(empty body)</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-center text-slate-500 py-12">Unable to load preview.</p>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setShowEmailPreview(false)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showSaveTemplate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowSaveTemplate(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Save as template</h3>
            <input
              type="text"
              value={saveTemplateName}
              onChange={(e) => setSaveTemplateName(e.target.value)}
              placeholder="Template name"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSaveTemplate(false)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate}
                className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              >
                <SafeIcon icon={FiFileText} className="w-4 h-4" />
                {savingTemplate ? 'Saving…' : 'Save'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowConfirm(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Confirm send</h3>
            <p className="text-slate-600 mb-4 text-sm leading-relaxed">
              Queue <strong>{totalQueueRows}</strong> email{totalQueueRows !== 1 ? 's' : ''} for{' '}
              <strong>{emailableCount}</strong> recipient
              {emailableCount !== 1 ? 's' : ''}
              {scheduleMode === 'immediate'
                ? ' (send as soon as the queue processor runs)'
                : scheduleMode === 'multiple'
                  ? ` across ${buildSendAtTimes().length} scheduled time(s)`
                  : ' at the scheduled time'}
              ? Merge tags will be resolved per recipient.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50"
              >
                {sending ? 'Queuing…' : 'Confirm'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default MarketingQuickSend;
